import React, { useContext, useEffect, useState } from 'react'
import { AppContext } from '../../../../global'
import * as actions from '../store/actions'
import { Contact, emptyContactForm } from '../store/store'
import ContactRowCompact from './ContactRowCompact'
import ContactRow from './ContactRow'
import './Contacts.css'
import { useTranslation } from 'react-i18next'
import ContactForm from './ContactForm'
import { ButtonPrimary } from '../../../../components/atoms'
import EmptyList from '../../../../components/EmptyList'
import RequestStateContainer from '../../../../components/containers/RequestStateContainer'

type Props = {
  accountId: string
}

function Contacts(props: Props) {
  const { accountId } = props
  const { state, execute, confirm } = useContext(AppContext)
  const contactsSettingsState = state.settings.contacts
  const contacts = contactsSettingsState.contacts
  const [contactFormOpen, setContactFormOpen] = useState(false)
  const [editContactId, setEditContactId] = useState<string | null>(null)
  const { t } = useTranslation(['global', 'settings_contacts', 'form'])

  useEffect(() => {
    execute(actions.loadContacts(accountId))
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [])

  function removeContact(contactId: string) {
    confirm({
      title: t('global:confirm'),
      text: t('settings_contacts:confirm_delete'),
      callback: () => {
        execute(actions.removeContact(accountId, contactId))
      }
    })
  }

  async function onContactEdit(contact: Contact) {
    setEditContactId(contact.id)

    const contactForm = {
      name: contact.name,
      email: contact.email,
      phone: contact.phone,
      mobile: contact.mobile
    }

    execute(actions.updateAddForm(contactForm))
    setContactFormOpen(true)
  }

  async function openAddContact() {
    setEditContactId(null)
    await execute(actions.updateAddForm(emptyContactForm))
    setContactFormOpen(true)
  }

  function onContactSave() {
    setContactFormOpen(false)
    execute(actions.loadContacts(accountId))
  }

  return (
    <div>
      {contactFormOpen && (
        <ContactForm
          accountId={accountId}
          contactId={editContactId}
          onClose={() => setContactFormOpen(false)}
          onSave={onContactSave} />)}

      <RequestStateContainer requestState={contactsSettingsState.loadRequestState} errorText={t('settings_contacts:cannot_load')}>
        <div>
          {contacts.length === 0 && (
            <EmptyList message={t('settings_contacts:no_contacts')} icon="fas fa-users" addButtonInfo={{
              text: t('settings_contacts:add_contact'),
              onClick: openAddContact
            }} />
          )}

          {contacts.length > 0 && (
            <>
              <div className="padding-s">
                <ButtonPrimary data-cy="btn-add-contact" onClick={openAddContact}>{t('settings_contacts:add_contact')}</ButtonPrimary>
              </div>

              <table id="contacts-table" className="content-table" cellSpacing={0} cellPadding={5}>
                <thead>
                  <tr>
                    <th>{t('form:name')}</th>
                    <th>{t('form:email')}</th>
                    <th>{t('form:phone')}</th>
                    <th>{t('form:mobile')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact: Contact) => (
                    <ContactRow onEdit={onContactEdit} key={contact.id} contact={contact} removeContact={removeContact} />
                  ))}
                </tbody>
              </table>

              <div id="contacts-compact">
                {contacts.map((contact: Contact) => (
                  <ContactRowCompact onEdit={onContactEdit} key={contact.id} contact={contact} removeContact={removeContact} />
                ))}
              </div>
            </>
          )}
        </div>
      </RequestStateContainer>
    </div>
  )
}

export default Contacts
